import {
  type FieldErrorMap,
  fieldSpecificMessages,
  parseApiError,
  toSpanishMessages,
} from "@/lib/api/error-messages"

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000"

export class ApiError extends Error {
  status: number
  /** Friendly, localized (Spanish) messages, ready to display. */
  errors: string[]
  /** Top-level backend error symbol, when present (e.g. "forbidden"). */
  code?: string
  /** Backend field identifier for a parameter-level validation error. */
  param?: string
  /** Per-field validation details, for callers that want field-level handling. */
  fieldErrors: FieldErrorMap

  constructor(
    status: number,
    errors: string[],
    opts: { code?: string; param?: string; fieldErrors?: FieldErrorMap } = {},
  ) {
    super(errors[0] ?? `Request failed with status ${status}`)
    this.name = "ApiError"
    this.status = status
    this.errors = errors
    this.code = opts.code
    this.param = opts.param
    this.fieldErrors = opts.fieldErrors ?? {}
  }
}

/**
 * Build an {@link ApiError} from a raw backend body: parses the contract and
 * translates the codes into friendly Spanish messages in one place, so every
 * throw path (apiFetch, clientApiFetch, postAuth) is consistent.
 */
/** Code assigned to a 403 that never reached Rails (e.g. the WAF size rule). */
export const REQUEST_BLOCKED_CODE = "request_blocked"

export function toApiError(data: unknown, status: number): ApiError {
  const parsed = parseApiError(data, status)
  // Rails always answers a 403 with a JSON body carrying an `error` code. A
  // 403 with no parseable body was produced upstream of Rails (the AWS WAF
  // SizeRestrictions_BODY rule blocks bodies over 8 KB), so it must not read
  // as a permission error.
  if (status === 403 && data === null) parsed.code = REQUEST_BLOCKED_CODE
  return new ApiError(status, toSpanishMessages(parsed), {
    code: parsed.code,
    param: parsed.param,
    fieldErrors: parsed.fieldErrors,
  })
}

/**
 * Spanish message for the first known per-field validation code carried by an
 * error, or `undefined` for anything else (non-ApiError, no field errors, or
 * only unmapped codes). Lets a component keep its own fallback toast.
 */
export function knownFieldErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  return fieldSpecificMessages(error.fieldErrors)[0]
}

/**
 * Parse a response body as JSON, tolerating a non-JSON body (blank, plain
 * text, or an HTML error page — e.g. Rails' default routing-error page when
 * a route genuinely doesn't exist on whatever backend this environment
 * points to, such as while a backend PR is still pending merge/deploy).
 * Without this, `JSON.parse` throws a raw SyntaxError that isn't an
 * ApiError, so callers checking `error instanceof ApiError` never catch it
 * — it propagates as an unhandled exception instead of a clean error with
 * the real HTTP status attached.
 *
 * Also used on the SUCCESS path (a 200 with a non-JSON body is equally
 * malformed) — a caller that destructures a property off the result with no
 * null check can still throw, just at the call site instead of here. That's
 * an unchanged failure mode from before this helper existed (it was a raw
 * JSON.parse throw either way); this only moves where the null/undefined
 * shows up, it doesn't newly introduce the possibility.
 */
export function safeParseJson(text: string): unknown {
  if (text.length === 0) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

interface ApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: unknown
  serializedBody?: string
  redirect?: RequestRedirect
  token?: string | null
  headers?: Record<string, string>
  cache?: RequestCache
  signal?: AbortSignal
}

export interface ApiResponse<T> {
  data: T
  token: string | null
  status: number
}

export async function apiFetch<T>(
  path: string,
  {
    method = "GET",
    body,
    serializedBody,
    redirect,
    token,
    headers: extraHeaders,
    cache,
    signal,
  }: ApiFetchOptions = {},
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  }

  if (body !== undefined || serializedBody !== undefined) {
    headers["Content-Type"] = "application/json"
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  Object.assign(headers, extraHeaders)

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: serializedBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
    redirect,
    cache,
    signal,
  })

  const text = await response.text()
  const data = safeParseJson(text)

  if (!response.ok) {
    throw toApiError(data, response.status)
  }

  let responseToken: string | null = null

  const authHeader = response.headers.get("Authorization")
  if (authHeader?.startsWith("Bearer ")) {
    responseToken = authHeader.slice("Bearer ".length)
  }

  if (!responseToken) {
    const xAuthToken = response.headers.get("X-Auth-Token")
    if (xAuthToken) {
      responseToken = xAuthToken
    }
  }

  if (!responseToken && data && typeof data === "object") {
    const obj = data as Record<string, unknown>
    if (typeof obj.token === "string") {
      responseToken = obj.token
    }
  }

  return {
    data: data as T,
    token: responseToken,
    status: response.status,
  }
}
